import React, { useEffect, useState } from 'react'
import {
  Box,
  FormControlLabel,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import PersonIcon from '@mui/icons-material/Person'
import { useTranslation } from 'react-i18next'
import {
  ProgramInviteFamilyOptionsResponsePrograms,
  ProgramType,
} from '@swagger'
import TextButton, { TextButtonVariant } from '../../Buttons/TextButton'
import { dateToDashString } from '@utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '@utils/reinterpretYearMonthDayAsLocalTime'
import {
  ProgramInviteFamilyOptionsExtends,
  StudentProgramFormField,
  StudentProgramFormFieldsProps,
} from './types'
import {
  hasAvailableSpotsInPrograms,
  isProgramTypeInAvailableOptions,
} from './utils'

const StudentProgramFormFields: React.FC<StudentProgramFormFieldsProps> = (
  props
) => {
  const {
    studentFieldId,
    programFieldId,
    programFieldKey,
    programTypes,
    removeStudentProgramField,
    updateStudentForParent,
    handleAvailableSpots,
    handleRemoveOfferedInCurrentInvite,
    updateAllSelectors,
    availableSpots,
    verifyThatProgramHasAvailableSpots,
  } = props

  const { t } = useTranslation()
  const theme = useTheme()

  const [studentProgramFormState, setStudentProgramFormState] =
    useState<StudentProgramFormField>(() => {
      const firstProgram = programTypes[0]

      return {
        isApplicationFeePaid: false,
        programType: firstProgram?.programType ?? '',
        semesterOneStartDate: firstProgram
          ? dateToDashString(
              reinterpretYearMonthDayAsLocalTime(
                firstProgram.semesterOneStartDate
              )
            )
          : '',
        programKey: firstProgram?.programKey ?? 0,
        availableSpots: firstProgram?.availableSpots ?? 0,
        outOfSpots: firstProgram?.outOfSpots ?? false,
      }
    })

  const [isInitialLoad, setIsInitialLoad] = useState(true)

  useEffect(() => {
    if (isInitialLoad) {
      updateStudentForParent(studentProgramFormState, studentFieldId)
      setIsInitialLoad(false)
    }
  }, [
    isInitialLoad,
    studentFieldId,
    studentProgramFormState,
    updateStudentForParent,
  ])

  useEffect(() => {
    const selectedProgram = programTypes.find(
      (programType) =>
        verifyThatProgramHasAvailableSpots(programType, availableSpots) > 0
    )

    if (
      (studentProgramFormState.programType !== '' &&
        !isProgramTypeInAvailableOptions(
          studentProgramFormState.programType as ProgramType,
          programTypes
        ) &&
        selectedProgram) ||
      (studentProgramFormState.availableSpots <= 0 && selectedProgram) ||
      (studentProgramFormState.outOfSpots === true && selectedProgram)
    ) {
      setStudentProgramFormState((prev) => {
        const next: StudentProgramFormField = {
          ...prev,
          programType: selectedProgram.programType,
          semesterOneStartDate: dateToDashString(
            reinterpretYearMonthDayAsLocalTime(
              selectedProgram.semesterOneStartDate
            )
          ),
          programKey: selectedProgram.programKey,
          availableSpots: selectedProgram.availableSpots,
          outOfSpots: false,
        }

        updateStudentForParent(next, studentFieldId)

        return next
      })
    }
  }, [
    availableSpots,
    programTypes,
    studentFieldId,
    studentProgramFormState,
    updateStudentForParent,
    verifyThatProgramHasAvailableSpots,
  ])

  const searchProgramType = (
    programKey: number
  ): ProgramInviteFamilyOptionsResponsePrograms | undefined => {
    return programTypes.find(
      (programType) => programType.programKey === programKey
    )
  }

  const handleStudentProgramTypeChange = (
    event: SelectChangeEvent<unknown>
  ) => {
    const programKey = Number(event.target.value)

    const selectedProgram = searchProgramType(programKey)

    if (!selectedProgram) {
      return
    }

    const newStateObj: StudentProgramFormField = {
      ...studentProgramFormState,
      programType: selectedProgram.programType,
      semesterOneStartDate: dateToDashString(
        reinterpretYearMonthDayAsLocalTime(selectedProgram.semesterOneStartDate)
      ),
      programKey: selectedProgram.programKey,
      availableSpots: selectedProgram.availableSpots,
    }

    setStudentProgramFormState(newStateObj)

    updateStudentForParent(newStateObj, studentFieldId)
    handleAvailableSpots(selectedProgram, programFieldId)
    updateAllSelectors()
  }

  const hasAvailableSpots = hasAvailableSpotsInPrograms(programTypes)

  const isProgramDisabled = programTypes.length === 1 || !hasAvailableSpots

  const programTypeExists = programTypes.some(
    (programType) =>
      programType.programKey === studentProgramFormState.programKey
  )

  const programFieldValue =
    !!studentProgramFormState.programKey && programTypeExists
      ? studentProgramFormState.programKey
      : ''

  const selectedProgramFieldKey =
    !!studentProgramFormState.programKey &&
    !!studentProgramFormState.semesterOneStartDate
      ? studentProgramFormState.programKey
      : ''

  const handleApplicationFeePaidChange = () => {
    const updatedState = {
      ...studentProgramFormState,
      isApplicationFeePaid: !studentProgramFormState.isApplicationFeePaid,
    }

    setStudentProgramFormState(updatedState)
    updateStudentForParent(updatedState, studentFieldId)
  }

  const handleRemoveField = () => {
    removeStudentProgramField?.(studentFieldId)
    handleRemoveOfferedInCurrentInvite(programFieldId)
  }

  const isSelectorDisabled = (
    programType: ProgramInviteFamilyOptionsExtends
  ): boolean => {
    const isTheSameSelector = availableSpots.find((availableSpot) =>
      availableSpot.dropdownsKeys.includes(programFieldId)
    )

    if (isTheSameSelector) {
      return false
    }

    return programType.availableSpots === 0 || !!programType.outOfSpots
  }

  return (
    <>
      <Box display="flex" alignItems="center">
        <PersonIcon
          sx={{
            color: theme.palette.textOrIcon.personIcon,
            height: '48px',
            width: '48px',
          }}
        />

        <TextField
          id={programFieldId}
          key={selectedProgramFieldKey}
          label={t('Families.FamilyForm.FormField.Program', 'Program')}
          variant="filled"
          select={!isProgramDisabled}
          disabled={isProgramDisabled}
          value={programFieldValue}
          SelectProps={{ onChange: handleStudentProgramTypeChange }}
          fullWidth
        >
          {programTypes.map((programType) => (
            <MenuItem
              disabled={isSelectorDisabled(programType)}
              key={programType.programKey}
              value={programType.programKey}
            >
              {`${programType.programType} (${dateToDashString(
                reinterpretYearMonthDayAsLocalTime(
                  programType.semesterOneStartDate
                )
              )})`}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box
        sx={{
          gridColumn: '2 / span 2',
          display: 'grid',
          gridTemplateColumns: '50% 50%',
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: theme.spacing(1, 0),
          },
        }}
      >
        {!!studentProgramFormState.programType && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={studentProgramFormState.isApplicationFeePaid}
                  name="applicationFeePaid"
                  onChange={handleApplicationFeePaidChange}
                  sx={{
                    color: theme.palette.textOrIcon.checkbox,
                    padding: theme.spacing(0, 0, 0, 1),
                    [theme.breakpoints.down('sm')]: {
                      padding: theme.spacing(1, 0),
                    },
                  }}
                />
              }
              label={
                <Typography variant="subtitle2" component="p">
                  {t(
                    'Families.FamilyForm.Checkbox.ApplicationFeeReceived',
                    'Application fee received from family'
                  )}
                </Typography>
              }
            />

            <TextButton
              id={`removeButton-${programFieldKey}`}
              variant={TextButtonVariant.Remove}
              onClick={handleRemoveField}
            />
          </>
        )}
      </Box>
    </>
  )
}

export default StudentProgramFormFields
